import { useState, useRef, useEffect, useMemo } from "react";
import styled from "styled-components";

import { Form } from '../form/FormComponents';
import CountryMenuItem from "./CountryMenuItem";

import useTranslation from "../../hooks/useTranslation";
import { CountryFlag } from "./CountryMenuItem";
import useOutsideClick from "../../hooks/useOutsideClick";
import { exactCountry, fold } from "./exactCountry";
import { CheckIcon, CrossIcon } from '../generic/Validate'


const SearchContainer = styled.div`
    height: "100%";

    @media (max-width: ${({ theme }) => theme.breakpoint.xs}) {
        display: none;
    }
`

const InputFlag = styled(CountryFlag)`
  position: absolute;
  top: 50%;
  left: 5px;
  transform: translateY(-50%);
  z-index: 2;
`

const InputField = styled(Form.Control)`
    padding-left:45px;
`

const SearchResults = styled.div`
    position: absolute;
    top: 42px;
    width: 100%;
    max-height: 250px;
    overflow-y: auto;
    box-shadow: 0 2px 5px rgb(0 0 0 / 28%);
    z-index:101;

    &.visible {
      visibility: visible;
    }

    &.invisible {
        visibility: hidden!important;
    }
`;

const ListGroup = styled.ul`
    --list-group-color: ${({ theme }) => theme.font.color};
    --list-group-bg: ${({ theme }) => theme.dropdown.background};
    --list-group-border-color: ${({ theme }) => theme.dropdown.border};
    --list-group-border-width: 1px;
    --list-group-border-radius: 0.375rem;
    --list-group-item-padding-x: 1rem;
    --list-group-item-padding-y: 0.5rem;
    --list-group-action-color: ${({ theme }) => theme.font.color};
    --list-group-action-hover-color: ${({ theme }) => theme.dropdown.selectedFont};
    --list-group-action-hover-bg: ${({ theme }) => theme.dropdown.selectedBackground};
    --list-group-action-active-color: ${({ theme }) => theme.dropdown.selectedFont};
    --list-group-action-active-bg: ${({ theme }) => theme.dropdown.selectedBackground};
    --list-group-disabled-color: ${({ theme }) => theme.font.color};
    --list-group-disabled-bg: ${({ theme }) => theme.dropdown.background};
    --list-group-active-color: ${({ theme }) => theme.dropdown.selectedFont};
    --list-group-active-bg: ${({ theme }) => theme.dropdown.selectedBackground};
    --list-group-active-border-color: ${({ theme }) => theme.dropdown.selectedBackground};
    display: flex;
    flex-direction: column;
    background-color: var(--list-group-bg);
    padding-left: 0;
    margin-bottom: 0;
    border-radius: var(--list-group-border-radius);
    overflow-x: hidden;
`


const CountryControl = ({ data, onSelect, placeholder, valueId, valueName, handleBlur, isRed, flags, testId, name = 'country' }) => {
    const [isVisbile, setVisiblity] = useState(false);
    const [search, setSearch] = useState("");
    const [cursor, setCursor] = useState(-1);

    const searchResultRef = useRef(null);

    const handleClickOutside = event => {
        hideSuggestion();
    };

    const myRef = useRef(null);
    useOutsideClick(myRef, handleClickOutside, 'modal');

    const { t } = useTranslation()

    // Load the value that is already in the DB
    useEffect(() => {
        setSearch(valueName)
    }, [valueName])

    const scrollIntoView = position => {
        searchResultRef.current.parentNode.scrollTo({
            top: position,
            behavior: "smooth"
        });
    };

    useEffect(() => {
        if (cursor < 0 || cursor > suggestions.length || !searchResultRef) {
            return () => { };
        }

        let listItems = Array.from(searchResultRef.current.children);
        listItems[cursor] && scrollIntoView(listItems[cursor].offsetTop);
    }, [cursor]);

    const suggestions = useMemo(() => {
        const list = data?.list || [];
        if (!search) return list;

        setCursor(-1);
        scrollIntoView(0);

        const searchLower = search.toLowerCase();
        return list.filter(item =>
            (searchLower.length >= 3 && item.name.toLowerCase().includes(searchLower)) ||
            item.id.toLowerCase().includes(searchLower)
        );
    }, [data?.list, search]);

    const showSuggestion = () => setVisiblity(true);
    const hideSuggestion = () => setVisiblity(false);

    const keyboardNavigation = e => {
        if (e.key === "ArrowDown") {
            isVisbile
                ? setCursor(c => (c < suggestions.length - 1 ? c + 1 : c))
                : showSuggestion();
        }

        if (e.key === "ArrowUp") {
            setCursor(c => (c > 0 ? c - 1 : 0));
        }

        if (e.key === "Escape") {
            hideSuggestion();
        }

        if (e.key === "Enter" && cursor >= 0) {
            e.preventDefault()
            setSearch(suggestions[cursor].name);
            hideSuggestion();
            onSelect(suggestions[cursor]);
        }
    };

    return (
        <SearchContainer ref={myRef}>
            <Form.Group name="Country">
                <InputFlag src={flags[valueId]}></InputFlag>
                <InputField
                    type="text"
                    id={name}
                    name={name}
                    value={search || ''}
                    onClick={showSuggestion}
                    onChange={e => {
                        const text = e.target.value
                        setSearch(text)
                        // A whole name, typed or autofilled, is a choice. Not the
                        // code yet: "In" on the way to "Indonesia" is India's.
                        const match = exactCountry(data?.list, text)
                        if (match && match.id !== valueId && fold(text) !== fold(match.id)) onSelect(match)
                    }}
                    onFocus={() => showSuggestion()}
                    onKeyDown={e => keyboardNavigation(e)}
                    autoComplete="off"
                    placeholder={t(placeholder)}
                    onBlur={e => {
                        // Leaving the field: a name or a code that names one
                        // country is that country; anything else is cleared.
                        const match = exactCountry(data?.list, e.target.value)
                        if (match) { if (match.id !== valueId) onSelect(match); return }
                        handleBlur?.(e)
                    }}
                    data-testid={testId}
                    {...(isRed && { required: true })}
                />
                {valueName && <CheckIcon></CheckIcon>}
                {!valueName && !isRed && <CrossIcon></CrossIcon>}
            </Form.Group>
            <SearchResults
                id='countries-search-result'
                className={`${isVisbile ? "visible" : "invisible"}`}
            >
                <ListGroup ref={searchResultRef} id='countries-menu'>
                    {suggestions.map((item, idx) => (
                        <CountryMenuItem
                            key={item.name}
                            onSelectItem={() => {
                                hideSuggestion();
                                setSearch(item.name);
                                onSelect(item);
                            }}
                            isHighlighted={cursor === idx}
                            {...item}
                            flag={flags[item.id]}
                        />
                    ))}
                </ListGroup>
            </SearchResults>
        </SearchContainer>
    );
};

export default CountryControl;
